import {
	CarrierName,
	City,
	carrierName,
	Dtos,
	NPN,
	Percent,
	ProductType,
	StreetAddress,
	USStateOrTerritory,
	UserId,
	usStateOrTerritoryDef,
	ZipCode,
} from "@inrev/common";
import { useAdminGetCarrierPreviews } from "@portal/api/account/admin";
import { useAdminFetchAdminUsers } from "@portal/api/admin-user/admin";
import { useAdminCreateAgency } from "@portal/api/agency/admin";
import { AdminCreateAgencyFormData } from "@portal/api/agency/admin/types";
import { useFlags } from "@portal/providers/GlobalFlagsProvider";
import { FC, useEffect } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useNavigate } from "react-router";
import z from "zod/v4";
import { formatName } from "../../../utils";
import { stripEmptyResolver, typedZodResolver } from "../../../utils/form";
import { FormField, FormFieldGroup, FormItemLabel, FormRow } from "../../form";
import { FormCheckboxOption, FormDropdown, FormInput, FormPercent } from "../../form/fields";
import { Button } from "../../ui/Button";
import { StatePicker } from "../../ui/StatePicker";

export type AdminCreateAgencyFormProps = {
	onClose: () => void;
};

export const AdminCreateAgencyForm: FC<AdminCreateAgencyFormProps> = ({ onClose }) => {
	const { adminUsers, adminUsersLoading, adminUsersError } = useAdminFetchAdminUsers();
	const { allCarriers, allCarriersIsLoading, allCarriersError } = useAdminGetCarrierPreviews();
	const { createAgency, createAgencyLoading, createAgencyError } = useAdminCreateAgency();
	const { showErrorFlag } = useFlags();
	const navigate = useNavigate();

	const agencySchema = Dtos.Admin.Agency.Create.Request.schema;
	const carrierAgencySchema = Dtos.Admin.CarrierAgencyRelationships.Create.Request.schema.extend({
		carrierName: carrierName,
	});

	const schema = z.object({
		agency: agencySchema,
		carrierAgencies: carrierAgencySchema.array(),
	});
	const stateOptions = usStateOrTerritoryDef.map((state) => ({
		label: state,
		value: state,
	}));
	const underwriterOptions =
		adminUsers?.map((adminUser) => ({
			label: formatName(adminUser) ?? "--",
			value: adminUser.id,
		})) || [];

	const defaultValues: AdminCreateAgencyFormData = {
		agency: {
			displayName: "",
			legalName: "",
			address: {
				street: "" as StreetAddress,
				city: "" as City,
				state: "CA" as USStateOrTerritory,
				zip: "" as ZipCode,
			},
			npn: "" as NPN,
			underwriterUserId: "" as UserId,
			licensedFor: usStateOrTerritoryDef.reduce(
				(acc, state) => ({ ...acc, [state]: ["surety"] }),
				{} as { [K in USStateOrTerritory]: ProductType[] },
			),
		},
		carrierAgencies: [],
	};

	const formMethods = useForm<
		AdminCreateAgencyFormData,
		any,
		{
			agency: Dtos.Admin.Agency.Create.Request;
			carrierAgencies: (Dtos.Admin.CarrierAgencyRelationships.Create.Request & {
				carrierName: CarrierName;
			})[];
		}
	>({
		defaultValues,
		reValidateMode: "onBlur",
		resolver: stripEmptyResolver(typedZodResolver(schema), true, true),
	});

	const { control, setValue, watch, handleSubmit } = formMethods;

	// Set the default carrier agencies when carriers are loaded
	useEffect(() => {
		if (allCarriers) {
			setValue(
				"carrierAgencies",
				allCarriers
					.sort((a, b) => a.displayName.localeCompare(b.displayName))
					.map((carrier) => ({
						carrierName: carrier.name,
						powersAndSeals: false,
						agencyCommissionPercentage: 0 as Percent,
					})),
			);
		}
	}, [allCarriers, setValue]);

	const licensedFor = watch("agency.licensedFor");

	const handleModalSubmit: SubmitHandler<AdminCreateAgencyFormData> = ({
		agency,
		carrierAgencies,
	}) => {
		createAgency({
			agency,
			carrierAgencies,
			onSuccess: (agencyId) => {
				onClose();
				navigate(`/agencies/${agencyId}`);
			},
			onError: (errors) => {
				errors.forEach((err) => {
					showErrorFlag({
						heading: "Upsert Carrier Agency Error",
						message: err.message,
					});
				});
			},
		});
	};

	return (
		<div className="flex flex-col p-6 bg-white rounded-md shadow-lg">
			{adminUsersLoading || adminUsersError || allCarriersError ? (
				<>
					{adminUsersLoading ? (
						<div>Loading...</div>
					) : (
						<div>Error loading agency data. Close and reload the page.</div>
					)}
				</>
			) : (
				<>
					<div className="flex flex-col min-w-[512px] max-w-xl mb-4 max-h-96 overflow-y-auto gap-4">
						{createAgencyError && (
							<div className="text-red-600 mb-4">
								Unable to create agency: {createAgencyError.message}
							</div>
						)}
						<FormField condensed>
							<FormItemLabel condensed required>
								Display Name
							</FormItemLabel>
							<FormInput
								control={control}
								name="agency.displayName"
								errorMessage="Invalid display name"
								testId="agency-display-name-input"
								compact
							/>
						</FormField>
						<FormField condensed>
							<FormItemLabel condensed required>
								Legal Name
							</FormItemLabel>
							<FormInput
								control={control}
								name="agency.legalName"
								errorMessage="Invalid legal name"
								testId="agency-legal-name-input"
								compact
							/>
						</FormField>
						<FormFieldGroup condensed>
							<FormField condensed>
								<FormItemLabel condensed required>
									Street
								</FormItemLabel>
								<FormInput
									control={control}
									name="agency.address.street"
									errorMessage="Invalid street"
									testId="agency-street-input"
									compact
								/>
							</FormField>
							<FormRow>
								<FormField condensed>
									<FormItemLabel condensed required>
										City
									</FormItemLabel>
									<FormInput
										control={control}
										name="agency.address.city"
										errorMessage="Invalid city"
										testId="agency-city-input"
										compact
									/>
								</FormField>
								<FormField condensed className="w-[100px] max-w-[100px]">
									<FormItemLabel condensed required>
										State
									</FormItemLabel>
									<FormDropdown
										control={control}
										name="agency.address.state"
										options={stateOptions}
										buttonClassName="relative"
										optionsClassName="absolute left-0 max-h-24 overflow-y-auto"
										errorMessage="Invalid state"
										testId="agency-state-dropdown"
										compact
									/>
								</FormField>
								<FormField condensed className="w-[100px] max-w-[100px]">
									<FormItemLabel condensed required>
										Zip
									</FormItemLabel>
									<FormInput
										control={control}
										name="agency.address.zip"
										onlyNumbers
										maxLength={5}
										errorMessage="Invalid zip"
										testId="agency-zip-input"
										compact
									/>
								</FormField>
							</FormRow>
						</FormFieldGroup>
						<FormField condensed>
							<FormItemLabel condensed required>
								National Producer Number
							</FormItemLabel>
							<FormInput
								control={control}
								name="agency.npn"
								compact
								errorMessage="Invalid NPN"
								testId="agency-npn-input"
							/>
						</FormField>
						<FormField condensed>
							<FormItemLabel condensed required>
								Licensed For
							</FormItemLabel>
							<StatePicker
								value={licensedFor}
								onChange={(state, checked) => {
									const newLicensedFor = {
										...licensedFor,
										[state]: checked ? ["surety"] : [],
									};

									setValue("agency.licensedFor", newLicensedFor);
								}}
							/>
						</FormField>
						<FormField condensed>
							<FormItemLabel condensed required>
								Underwriter Assignee
							</FormItemLabel>
							<FormDropdown
								control={control}
								name="agency.underwriterUserId"
								options={underwriterOptions}
								buttonClassName="relative"
								optionsClassName="absolute left-0 max-h-24 overflow-y-auto"
								errorMessage="Invalid underwriter"
								testId="agency-underwriter-dropdown"
								compact
							/>
						</FormField>
						<h2 className="text-xl mb-6">Carriers</h2>
						<hr />
						<FormFieldGroup condensed>
							{!allCarriersIsLoading && !allCarriersError && allCarriers ? (
								allCarriers.map((carrier, idx) => (
									<div key={carrier.name}>
										<div>{carrier.displayName}</div>
										<FormRow className="space-y-2">
											<div className="hidden">
												<FormInput control={control} name={`carrierAgencies.${idx}.carrierName`} />
											</div>
											<FormField condensed className="flex-[2]">
												<FormItemLabel condensed required>
													Agency Commission
												</FormItemLabel>
												<FormPercent
													control={control}
													name={`carrierAgencies.${idx}.agencyCommissionPercentage`}
													errorMessage="Invalid %"
													testId={`carrier-commission-${carrier.name}`}
													compact
												/>
											</FormField>
											<FormField condensed className="justify-start items-center gap-2">
												<FormItemLabel condensed noSpace>
													Powers & Seals
												</FormItemLabel>
												<FormCheckboxOption
													control={control}
													name={`carrierAgencies.${idx}.powersAndSeals`}
													testId={`carrier-powers-and-seals-${carrier.name}`}
												/>
											</FormField>
										</FormRow>
									</div>
								))
							) : (
								<>
									{allCarriersIsLoading ? (
										<div>Loading carriers...</div>
									) : (
										<div>Unable to load carriers, refresh the page and try again</div>
									)}
								</>
							)}
						</FormFieldGroup>
					</div>
					<Button
						onClick={handleSubmit(handleModalSubmit)}
						color="primary"
						disabled={createAgencyLoading}
						filled
						testId="create-agency-submit-button"
					>
						Create
					</Button>
				</>
			)}
		</div>
	);
};
